<template>
  <div>
    <el-alert
      title="可拖拽改程：按住色条左右拖动后松开保存；自动同步工单计划窗并刷新冲突。"
      type="info"
      :closable="false"
      show-icon
      style="margin-bottom:12px"
    />
    <div class="toolbar">
      <el-date-picker v-model="query.dateFrom" type="date" value-format="yyyy-MM-dd" placeholder="日期从" style="width:140px" />
      <el-date-picker v-model="query.dateTo" type="date" value-format="yyyy-MM-dd" placeholder="到" style="width:140px" />
      <el-input v-model="query.orderCode" clearable placeholder="工单编号" style="width:140px" />
      <el-select v-model="query.taskState" clearable placeholder="状态" style="width:110px">
        <el-option label="待开工" value="00" />
        <el-option label="进行中" value="01" />
        <el-option label="已完成" value="02" />
      </el-select>
      <el-input-number v-model="minDurationMin" :min="15" :max="1440" :step="15" controls-position="right" style="width:110px" />
      <span style="color:#909399;font-size:12px;margin-right:8px">最短空隙(分)</span>
      <el-button type="primary" :loading="loading" @click="load">刷新</el-button>
      <el-button @click="loadConflicts()">标冲突</el-button>
      <el-button :loading="gapsLoading" @click="loadGaps">空隙</el-button>
    </div>

    <div class="gantt-wrap" v-loading="loading">
      <div class="gantt-header">
        <div class="gantt-label">任务</div>
        <div class="gantt-scale" :style="{ width: scaleWidth + 'px' }">
          <div v-for="d in dayTicks" :key="d.key" class="gantt-day" :style="{ width: dayWidth + 'px' }">{{ d.label }}</div>
        </div>
      </div>
      <div class="gantt-body" v-if="bars.length">
        <div v-for="row in bars" :key="row.taskId" class="gantt-row">
          <div class="gantt-label" :title="row.taskCode">
            <div class="lbl-main">{{ row.orderCode }} / {{ row.operationName || '-' }}</div>
            <div class="lbl-sub">{{ row.taskCode }} · {{ stateLabel(row.taskState) }}</div>
          </div>
          <div class="gantt-track" :style="{ width: scaleWidth + 'px' }">
            <div
              class="gantt-bar"
              :class="['st-' + row.taskState, { conflict: conflictIds[row.taskId], dragging: drag && drag.taskId === row.taskId, draggable: row.taskState !== '02' && row.taskState !== '03' }]"
              :style="barStyle(row)"
              :title="tooltip(row)"
              @mousedown="onBarDown($event, row)"
            >{{ row.operationName || row.taskCode }}</div>
          </div>
        </div>
      </div>
      <div v-else class="gantt-empty">暂无已排程任务（需有计划开始/结束时间）</div>
    </div>

    <el-drawer title="产线空隙" :visible.sync="gapsVisible" size="480px" append-to-body>
      <el-table :data="gaps" border size="small" v-loading="gapsLoading" height="100%">
        <el-table-column prop="productionLineName" label="产线" min-width="120" />
        <el-table-column prop="fromDate" label="空闲起" width="160" />
        <el-table-column prop="toDate" label="空闲止" width="160" />
        <el-table-column prop="durationMin" label="分钟" width="70" align="right" />
      </el-table>
      <div v-if="!gapsLoading && !gaps.length" style="padding:16px;color:#909399">当前范围无满足最短时长的空隙</div>
    </el-drawer>
  </div>
</template>

<script>
import { listGanttBars, findScheduleConflicts, updateSchedule, listTimeGaps } from '../../api/schedule'

export default {
  name: 'GanttPage',
  data() {
    const today = new Date()
    const from = new Date(today.getTime() - 3 * 86400000)
    const to = new Date(today.getTime() + 14 * 86400000)
    const fmt = d => {
      const y = d.getFullYear()
      const m = ('0' + (d.getMonth() + 1)).slice(-2)
      const day = ('0' + d.getDate()).slice(-2)
      return y + '-' + m + '-' + day
    }
    return {
      query: { dateFrom: fmt(from), dateTo: fmt(to), orderCode: '', taskState: '' },
      bars: [], loading: false, dayWidth: 48, rangeStart: null, rangeEnd: null,
      conflictIds: {}, drag: null, minDurationMin: 60,
      gapsVisible: false, gapsLoading: false, gaps: []
    }
  },
  computed: {
    dayTicks() {
      if (!this.rangeStart || !this.rangeEnd) return []
      const ticks = []
      const cur = new Date(this.rangeStart.getFullYear(), this.rangeStart.getMonth(), this.rangeStart.getDate())
      const end = new Date(this.rangeEnd.getFullYear(), this.rangeEnd.getMonth(), this.rangeEnd.getDate())
      while (cur <= end) {
        ticks.push({ key: cur.getTime(), label: (cur.getMonth() + 1) + '/' + cur.getDate() })
        cur.setDate(cur.getDate() + 1)
      }
      return ticks
    },
    scaleWidth() { return Math.max(this.dayTicks.length, 1) * this.dayWidth },
    totalMs() {
      if (!this.rangeStart || !this.rangeEnd) return 1
      return Math.max(this.rangeEnd.getTime() + 86400000 - this.rangeStart.getTime(), 1)
    }
  },
  created() { this.load() },
  beforeDestroy() { this.unbindDrag() },
  methods: {
    stateLabel(s) { return ({ '00': '待开工', '01': '进行中', '02': '已完成', '03': '已取消' })[s] || s },
    parseDate(v) {
      if (!v) return null
      if (v instanceof Date) return v
      const s = String(v).replace('T', ' ').replace(/-/g, '/')
      const d = new Date(s)
      return isNaN(d.getTime()) ? null : d
    },
    fmtDateTime(d) {
      if (!d) return ''
      const p = n => (n < 10 ? '0' + n : '' + n)
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) +
        ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds())
    },
    load() {
      this.loading = true
      listGanttBars(this.query).then(res => {
        this.bars = res.data || []
        this.computeRange()
        this.loadConflicts(true)
      }).finally(() => { this.loading = false })
    },
    computeRange() {
      let min = this.parseDate(this.query.dateFrom + ' 00:00:00')
      let max = this.parseDate(this.query.dateTo + ' 23:59:59')
      this.bars.forEach(b => {
        const s = this.parseDate(b.planStart)
        const e = this.parseDate(b.planEnd)
        if (s && (!min || s < min)) min = s
        if (e && (!max || e > max)) max = e
      })
      if (!min) min = new Date()
      if (!max || max <= min) max = new Date(min.getTime() + 7 * 86400000)
      this.rangeStart = new Date(min.getFullYear(), min.getMonth(), min.getDate())
      this.rangeEnd = new Date(max.getFullYear(), max.getMonth(), max.getDate())
    },
    barStyle(row) {
      const preview = this.drag && this.drag.taskId === row.taskId
      const s = preview ? this.drag.newStart : this.parseDate(row.planStart)
      const e = preview ? this.drag.newEnd : this.parseDate(row.planEnd)
      if (!s || !e || !this.rangeStart) return { display: 'none' }
      const left = Math.max(0, (s.getTime() - this.rangeStart.getTime()) / this.totalMs)
      const right = Math.min(1, (e.getTime() - this.rangeStart.getTime()) / this.totalMs)
      const width = Math.max(right - left, 0.01)
      return { left: (left * 100) + '%', width: (width * 100) + '%' }
    },
    tooltip(row) {
      return (row.orderCode || '') + ' ' + (row.operationName || '') + '\n' +
        (row.planStart || '') + ' ~ ' + (row.planEnd || '') + '\n' +
        this.stateLabel(row.taskState) +
        (row.taskState !== '02' && row.taskState !== '03' ? '\n拖拽可改程' : '')
    },
    loadConflicts(silent) {
      findScheduleConflicts({
        orderCode: this.query.orderCode,
        dateFrom: this.query.dateFrom,
        dateTo: this.query.dateTo
      }).then(res => {
        const ids = {}
        ;(res.data || []).forEach(c => { ids[c.taskId] = true })
        this.conflictIds = ids
        if (!silent) {
          this.$message[Object.keys(ids).length ? 'warning' : 'success'](
            Object.keys(ids).length ? ('标出 ' + Object.keys(ids).length + ' 条冲突') : '未发现冲突'
          )
        }
      })
    },
    loadGaps() {
      this.gapsVisible = true
      this.gapsLoading = true
      listTimeGaps({
        dateFrom: this.query.dateFrom,
        dateTo: this.query.dateTo,
        minDurationMin: this.minDurationMin
      }).then(res => {
        this.gaps = res.data || []
      }).finally(() => { this.gapsLoading = false })
    },
    onBarDown(e, row) {
      if (row.taskState === '02' || row.taskState === '03') return
      if (e.button !== 0) return
      e.preventDefault()
      const s = this.parseDate(row.planStart)
      const end = this.parseDate(row.planEnd)
      if (!s || !end) return
      this.drag = {
        taskId: row.taskId, version: row.version, startX: e.clientX,
        originStart: s, originEnd: end, duration: end.getTime() - s.getTime(),
        newStart: s, newEnd: end
      }
      document.addEventListener('mousemove', this.onBarMove)
      document.addEventListener('mouseup', this.onBarUp)
    },
    onBarMove(e) {
      if (!this.drag || !this.rangeStart) return
      const dx = e.clientX - this.drag.startX
      const deltaMs = (dx / this.scaleWidth) * this.totalMs
      const snap = 15 * 60 * 1000
      const ns = Math.round((this.drag.originStart.getTime() + deltaMs) / snap) * snap
      this.drag.newStart = new Date(ns)
      this.drag.newEnd = new Date(ns + this.drag.duration)
    },
    onBarUp() {
      if (!this.drag) return
      const d = this.drag
      this.unbindDrag()
      const moved = Math.abs(d.newStart.getTime() - d.originStart.getTime()) > 60 * 1000
      if (!moved) { this.drag = null; return }
      const startStr = this.fmtDateTime(d.newStart)
      const endStr = this.fmtDateTime(d.newEnd)
      this.$confirm('将任务改程为：\n' + startStr + '\n~ ' + endStr, '确认改程', {
        type: 'warning', confirmButtonText: '保存', cancelButtonText: '取消'
      }).then(() => {
        updateSchedule({
          taskId: d.taskId, version: d.version, planStart: startStr, planEnd: endStr
        }).then(() => {
          this.drag = null
          this.$message.success('改程已保存')
          this.load()
        }).catch(() => { this.drag = null })
      }).catch(() => { this.drag = null })
    },
    unbindDrag() {
      document.removeEventListener('mousemove', this.onBarMove)
      document.removeEventListener('mouseup', this.onBarUp)
    }
  }
}
</script>

<style scoped>
.gantt-wrap { border: 1px solid #ebeef5; background: #fff; overflow-x: auto; }
.gantt-header, .gantt-row { display: flex; min-width: 100%; border-bottom: 1px solid #ebeef5; }
.gantt-header { position: sticky; top: 0; z-index: 2; background: #f5f7fa; font-size: 12px; color: #909399; }
.gantt-label {
  flex: 0 0 240px; width: 240px; padding: 8px 10px; border-right: 1px solid #ebeef5;
  box-sizing: border-box; background: #fff; position: sticky; left: 0; z-index: 1;
}
.gantt-header .gantt-label { background: #f5f7fa; z-index: 3; }
.lbl-main { font-size: 13px; color: #303133; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lbl-sub { font-size: 12px; color: #909399; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gantt-scale, .gantt-track {
  position: relative; height: 44px;
  background-image: repeating-linear-gradient(to right, transparent 0, transparent 47px, #ebeef5 47px, #ebeef5 48px);
}
.gantt-scale { display: flex; height: 36px; align-items: center; }
.gantt-day { flex: 0 0 auto; text-align: center; box-sizing: border-box; }
.gantt-track { height: 52px; }
.gantt-bar {
  position: absolute; top: 12px; height: 28px; line-height: 28px; padding: 0 8px;
  border-radius: 3px; font-size: 12px; color: #fff; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; box-sizing: border-box; background: #409EFF; user-select: none;
}
.gantt-bar.draggable { cursor: grab; }
.gantt-bar.dragging { cursor: grabbing; opacity: 0.85; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
.gantt-bar.st-00 { background: #909399; }
.gantt-bar.st-01 { background: #409EFF; }
.gantt-bar.st-02 { background: #67C23A; cursor: default; }
.gantt-bar.conflict { box-shadow: 0 0 0 2px #F56C6C inset; }
.gantt-empty { padding: 24px; color: #909399; text-align: center; }
</style>
